/**
 * The hosted repos and their settings, for the Repos view and the command
 * strip under a tmux terminal. Module-level, like lib/repos.ts, so a command
 * saved in one shows up in the other without a refetch.
 */
import { ref } from "vue";
import { api } from "./api.js";
import { branchesOf, withMergeAuto, type RepoBranchSet, type RepoCommand, type RepoInfo, type RepoSettings } from "@shared/repoSettings.js";
import type { StartMode } from "@shared/startMode.js";
import type { QueuedRun } from "@shared/deploy.js";
import { queuedHere } from "./deploys.js";
import { toast } from "./toast.js";
import { keep, readKept } from "./tmuxCache.js";

/** This browser's last list until the first read: only to draw with. */
export const repoInfos = ref<RepoInfo[]>(readKept("repos") ?? []);
export const repoInfosLoaded = ref(false);
/** Why the list could not be had — most often, the bridge is not running. */
export const repoInfosError = ref<string | null>(null);

let inflight: Promise<void> | null = null;

export function loadRepoInfos(): Promise<void> {
  inflight ??= api
    .get<{ repos: RepoInfo[] }>("/api/tmux/repos")
    .then(({ repos }) => {
      repoInfos.value = repos;
      repoInfosError.value = null;
      keep("repos", repos);
    })
    .catch((err: unknown) => {
      repoInfosError.value = err instanceof Error ? err.message : String(err);
    })
    .finally(() => {
      repoInfosLoaded.value = true;
      inflight = null;
    });
  return inflight;
}

/** A new, empty repo on the git server (the git user's `newrepo`), in the first git folder of
 *  `team` (the board on screen; null, Personal, is the default folder); the list is reread with it in. */
export async function createRepo(name: string, team: string | null): Promise<void> {
  const { repos } = await api.post<{ repos: RepoInfo[] }>("/api/tmux/repos", { name, team });
  repoInfos.value = repos;
  repoInfosError.value = null;
}

/** A repo deleted: its bare repo on the git server and the claude user's checkout moved
 *  into trash folders, its settings gone; the list is reread without it. */
export async function deleteRepo(name: string): Promise<void> {
  const { repos } = await api.del<{ repos: RepoInfo[] }>(`/api/tmux/repos/${encodeURIComponent(name)}`);
  repoInfos.value = repos;
  repoInfosError.value = null;
}

/**
 * Whether the board of `team` lists a repo: its git folder is one of the team's
 * (shared/repoFolders.ts). Personal (null) lists the repos of every team the reader
 * is in (`mine`), as `/api/repos` does — or every repo when they are in none. An older
 * server says nothing, and then every board lists it, as before.
 */
export const repoOnBoard = (info: RepoInfo, team: string | null, mine: readonly { id: string }[]): boolean =>
  !info.teams || (team === null ? !mine.length || mine.some((t) => info.teams!.includes(t.id)) : info.teams.includes(team));

export async function saveRepoSettings(repo: string, settings: RepoSettings): Promise<RepoSettings> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}`, settings)).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    info.settings = saved;
    info.saved = true;
  }
  return saved;
}

/** Turn a repo's auto-deploy on or off, touching nothing else in its settings. */
export async function setAutoDeploy(repo: string, on: boolean): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/auto-deploy`, { on })).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    // The trunk's box in the branch settings comes with it.
    info.settings = { ...info.settings, autoDeploy: !!saved.autoDeploy, ...(saved.branches ? { branches: saved.branches, merges: saved.merges } : {}) };
    info.saved = true;
  }
}

/** The fields a branches save changes: the branches and links, and the flags read off them. */
const BRANCH_FIELDS = ["branches", "merges", "autoMerge", "autoPush", "autoPull", "autoDeployStaging", "autoDeploy"] as const;

/** Save a repo's branches and the links between them (the Repos view's
 *  Branches), touching nothing else in its settings. */
export async function setBranches(repo: string, set: RepoBranchSet): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/branches`, set)).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    const next: RepoSettings = { ...info.settings };
    for (const k of BRANCH_FIELDS) delete next[k];
    for (const k of BRANCH_FIELDS) if (saved[k] !== undefined) (next as unknown as Record<string, unknown>)[k] = saved[k];
    info.settings = next;
    info.saved = true;
  }
}

/** One link's auto ticked or not — "Auto deploy to MAIN" is staging → main's —
 *  touching nothing else. */
export function setMergeAuto(repo: string, from: string, into: string, on: boolean): Promise<void> {
  const info = repoInfos.value.find((r) => r.name === repo);
  return setBranches(repo, withMergeAuto(branchesOf(info?.settings), from, into, on));
}

/** Turn a repo's auto-commit on or off, touching nothing else in its settings. */
export async function setAutoCommit(repo: string, on: boolean): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/auto-commit`, { on })).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    const { autoCommit: _, ...rest } = info.settings;
    info.settings = saved.autoCommit ? { ...rest, autoCommit: true } : rest;
    info.saved = true;
  }
}

/** Turn a repo's auto-fix on or off, touching nothing else in its settings. */
export const setAutoFix = (repo: string, on: boolean): Promise<void> => putFix(repo, "auto-fix", { on });

/** Set a repo's start limit (Ready: none), touching nothing else in its settings. */
export const setStartLimit = (repo: string, mode: StartMode): Promise<void> => putFix(repo, "start-limit", { mode });

/** Set a repo's "Max Claudes" (null: none), touching nothing else in its settings. */
export async function setMaxClaudes(repo: string, max: number | null): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/max-claudes`, { max })).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    const { maxClaudes: _, ...rest } = info.settings;
    info.settings = saved.maxClaudes ? { ...rest, maxClaudes: saved.maxClaudes } : rest;
    info.saved = true;
  }
}

/** Turn a repo's quick deploy on or off, touching nothing else in its settings. */
export async function setQuickDeploy(repo: string, on: boolean): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/quick-deploy`, { on })).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    const { quickDeploy: _, ...rest } = info.settings;
    info.settings = saved.quickDeploy ? { ...rest, quickDeploy: true } : rest;
    info.saved = true;
  }
}

/** Either: both write auto-fix's derived mode and the limit. */
async function putFix(repo: string, route: string, body: object): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/${route}`, body)).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    const { autoFix: _, autoFixMode: __, startLimit: ___, ...rest } = info.settings;
    info.settings = {
      ...rest,
      ...(saved.autoFixMode ? { autoFixMode: saved.autoFixMode, autoFix: true } : {}),
      ...(saved.startLimit ? { startLimit: saved.startLimit } : {}),
    };
    info.saved = true;
  }
}

/** Star or unstar one of a repo's worktrees, touching nothing else in its settings. */
export async function setStarred(repo: string, worktree: string, on: boolean): Promise<void> {
  const saved = (await api.put<{ settings: RepoSettings }>(`/api/tmux/repos/${encodeURIComponent(repo)}/star`, { worktree, on })).settings;
  const info = repoInfos.value.find((r) => r.name === repo);
  if (info) {
    const { starred: _, ...rest } = info.settings;
    info.settings = saved.starred ? { ...rest, starred: saved.starred } : rest;
    info.saved = true;
  }
}

/** Run one of a repo's commands; answers with the tmux session it started.
 *  `commit`: for the git panel's commit menu (`COMMIT_COMMANDS`), in the checkout. */
export async function runRepoCommand(
  repo: string,
  cmd: RepoCommand,
  message: string,
  worktree: string | null = null,
  commit: string | null = null,
): Promise<string> {
  const { session } = await api.post<{ session: string }>(`/api/tmux/repos/${encodeURIComponent(repo)}/run`, {
    commandId: cmd.id,
    message,
    worktree,
    ...(commit ? { commit } : {}),
  });
  return session;
}

/** As `runRepoCommand`, for the git panel's menus: one a running Deploy holds
 *  (`heldByDeploy`) waits for it instead of being refused, and runs once it
 *  ends (server/runQueue.ts). The session it started, or null when it waits. */
export async function runOrQueue(
  repo: string,
  cmd: RepoCommand,
  message: string,
  worktree: string | null = null,
  commit: string | null = null,
): Promise<string | null> {
  const answered = await api.post<{ session?: string; queued?: QueuedRun }>(`/api/tmux/repos/${encodeURIComponent(repo)}/run`, {
    commandId: cmd.id,
    message,
    worktree,
    queue: true,
    ...(commit ? { commit } : {}),
  });
  if (answered.queued) {
    queuedHere.add(answered.queued.id);
    toast(`${cmd.label}: queued — it runs once the deploy of ${repo} finishes`, 4000);
    return null;
  }
  return answered.session!;
}

/** Take a queued command back before its deploy ends. */
export async function cancelQueued(repo: string, id: string): Promise<void> {
  await api.del(`/api/tmux/repos/${encodeURIComponent(repo)}/queue/${encodeURIComponent(id)}`);
  queuedHere.delete(id);
}
